import { notify,
	type Message,
	type SDK_Colors,
	type SDK_Filter,
	StyledPillButton,
	clientConfig,
	colors,
	focusVisible,
	getSdk,
	strings,
	text,
} from "@quickpoint.me/shared";
import { getAuth, getIdToken } from "firebase/auth";
import { HostId, OwnerName, StackId } from "../../state/uploaderState";

import { useGSAP } from "@gsap/react";
import { GraphQLClient } from "graphql-request";
import gsap from "gsap";
import { useRef, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import styled from "styled-components";
import AiIcon from "../../icons/ai.svg?react";
import { FileTypeToDisplay } from "../../state/fileState";

const StyledAiWrap = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
`;

const StyledBack = styled.button.attrs({ type: "button" })`
  align-self: flex-start;
  background: none;
  border: none;
  cursor: pointer;
  color: #5335c9;
  font-family: ${(props) => props.theme.font};
  font-size: 0.85rem;
  font-weight: 500;
  padding: 0.3rem 0.4rem;
  margin: 0.5rem 0 0 0.5rem;
  border-radius: 6px;
  min-height: 44px;
  ${focusVisible}

  &:hover {
    text-decoration: underline;
  }
`;

const StyledAiImageGenerator = styled.div`
  padding: 1rem;
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  flex: 1;
  min-height: 0;
  font-family: ${(props) => props.theme.font};

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const StyledPromptPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const StyledPromptTextarea = styled.textarea`
  padding: 0.5rem;
  border: 1px solid #757575; /* 3:1 on white (WCAG 1.4.11) — #ccc was 1.6:1 */
  border-radius: 4px;
  width: 100%;
  min-height: 150px;
  resize: vertical;
  font-family: inherit;
  ${focusVisible}

  &::placeholder {
    color: ${colors.textSafe.grey}; /* 7:1 (AAA) */
    opacity: 1;
  }
`;

const StyledZoomButton = styled.button.attrs({ type: "button" })`
  background: none;
  border: none;
  padding: 0;
  cursor: zoom-in;
  max-width: 100%;
  max-height: 90%;
  ${focusVisible}
`;

const StyledPreviewPanel = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border: none;
  border-radius: 4px;
  min-height: 250px;
  position: relative;
  overflow: hidden;
  flex-direction: column;
`;

const StyledImagePreview = styled.img`
  max-width: 100%;
  max-height: 90%;
  object-fit: contain;
  border-radius: 4px;
  cursor: zoom-in;
`;

const ButtonContainer = styled.div`
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  display: flex;
  gap: 0.5rem;
`;

const StyledModalOverlay = styled.div`
  position: fixed; /* Position relative to the viewport */
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000; /* High z-index to be on top */
  cursor: zoom-out;

  img {
    width: 80vw;
    height: 80vh;
    object-fit: contain;
  }
`;

const responseChannel = new BroadcastChannel(strings.broadcastChannel.toUploader);

function AiImageView() {
	const [prompt, setPrompt] = useState("");
	const [loading, setLoading] = useState(false);
	const [imageUrl, setImageUrl] = useState<string | null>(null);
	const [imageName, setImageName] = useState<string | null>(null);

	const [isZoomed, setIsZoomed] = useState(false);
	const [useContext, _setUseContext] = useState(true);
	const hostId = useRecoilValue(HostId);
	const ownerName = useRecoilValue(OwnerName);
	const stackId = useRecoilValue(StackId);
	const setFileTypeToDisplay = useSetRecoilState(FileTypeToDisplay);
	const aiIconRef = useRef(null);

	useGSAP(
		() => {
			if (loading) {
				gsap.to(aiIconRef.current, {
					opacity: 0.3,
					duration: 2,
					filter: "hue-rotate(360deg)",
					repeat: -1,
					yoyo: true,
				});
			} else {
				gsap.to(aiIconRef.current, {
					opacity: 1,
					duration: 0.5,
					filter: "hue-rotate(0deg)",
					repeat: 0,
					yoyo: false,
				});
			}
		},
		{ dependencies: [loading], scope: aiIconRef },
	);

	const handleGenerate = async () => {
		if (!prompt) return;
		setLoading(true);
		setImageUrl(null);
		setIsZoomed(false);

		try {
			const user = getAuth().currentUser;
			if (!user) throw new Error("No user");
			const jwt = await getIdToken(user);
			const client = new GraphQLClient(clientConfig.gqlGenerateAiImage());
			const sdk = getSdk(client);

			const contextPromise = new Promise<{
				code?: string;
				filter?: SDK_Filter;
			}>((resolve, reject) => {
				const toAuthorBC = new BroadcastChannel(
					strings.broadcastChannel.author,
				);
				const message: Message = {
					hostId,
					type: "fileUploader",
					action: strings.messages.getCodeAndFilter,
				};
				toAuthorBC.postMessage(message);
				toAuthorBC.close();

				const timeout = setTimeout(() => {
					reject(new Error("Timeout: Did not receive code and filter from author."));
				}, 5000);

				responseChannel.onmessage = (event: MessageEvent<Message>) => {
					if (event.data.action === strings.messages.codeAndFilter) {
						clearTimeout(timeout);
						resolve(
							event.data.payload as unknown as {
								code: string;
								colors: SDK_Colors;
							},
						);
					}
				};
			});

			const { code, filter } = await contextPromise;

			const result = await sdk.GenerateAiImage({
				prompt,
				jwt,
				ownerName,
				stackId,
				useContext,
				code,
				filter: filter ? JSON.stringify(filter) : undefined,
			});

			if (
				result.generateAiImage?.__typename === "QpFile" &&
				result.generateAiImage.url
			) {
				setImageUrl(result.generateAiImage.url);
				setImageName(result.generateAiImage.userDefinedName);
				const toAuthorBC = new BroadcastChannel(
					strings.broadcastChannel.author,
				);
				const message: Message = {
					hostId,
					type: "fileUploader",
					action: strings.messages.updateFiles,
				};
				toAuthorBC.postMessage(message);
				toAuthorBC.close();
			} else {
				const errorMessage = "An unknown error occurred.";
				console.error("Failed to generate image:", result.generateAiImage);
				notify(`Failed to generate image: ${errorMessage}`);
			}
		} catch (error) {
			console.error("Error generating image:", error);
			setLoading(false);
			notify("An unexpected error occurred while generating the image.");
		} finally {
			setLoading(false);
		}
	};

	const handleClear = () => {
		setPrompt("");
		setImageUrl(null);
		setIsZoomed(false);
	};

	return (
		<StyledAiWrap>
			<StyledBack onClick={() => setFileTypeToDisplay("image")}>
				← {text.fileUploader.searchImagesBack}
			</StyledBack>
			<StyledAiImageGenerator>
				<StyledPromptPanel>
					<StyledPromptTextarea
						value={prompt}
						onChange={(e) => setPrompt(e.target.value)}
						aria-label={text.fileUploader.aiPrompt}
						placeholder={text.fileUploader.aiPlaceholder}
						disabled={loading}
					/>
					<StyledPillButton
						onClick={handleGenerate}
						disabled={loading || !prompt}
						style={{ alignSelf: "flex-start" }}
					>
						{loading ? text.fileUploader.generating : text.fileUploader.generate}
					</StyledPillButton>
				</StyledPromptPanel>
				<StyledPreviewPanel>
					{!imageUrl && (
						<div ref={aiIconRef}>
							<AiIcon style={{ width: "80px", height: "80px" }} />
						</div>
					)}
					{imageUrl && (
						<>
							<StyledZoomButton
								onClick={() => setIsZoomed(true)}
								aria-label={text.fileUploader.zoomIn}
							>
								<StyledImagePreview src={imageUrl} alt={prompt} />
							</StyledZoomButton>
							{imageName}
							<ButtonContainer>
								<StyledPillButton onClick={handleClear}>
									{text.fileUploader.clear}
								</StyledPillButton>
							</ButtonContainer>
						</>
					)}
				</StyledPreviewPanel>
			</StyledAiImageGenerator>

			{isZoomed && imageUrl && (
				<StyledModalOverlay
					as="button"
					type="button"
					autoFocus
					aria-label={text.fileUploader.zoomOut}
					onClick={() => setIsZoomed(false)}
					onKeyDown={(e: React.KeyboardEvent) => {
						if (e.key === "Escape") setIsZoomed(false);
					}}
				>
					<img src={imageUrl} alt={prompt} />
				</StyledModalOverlay>
			)}
		</StyledAiWrap>
	);
}

export default AiImageView;
